/**
 * Cliente HTTP del **portal del cliente**.
 *
 * Versión reducida del cliente del panel (`edsuite-web/src/lib/http/api-client.ts`)
 * adaptada a que el portal es una aplicación aparte:
 *
 *  - **Sin `SessionProvider` ni refresco de token**: el portal no tiene refresh
 *    token. Su token (`scope: 'cliente'`) lo pone cada petición del portal en las
 *    cabeceras (`portal.api.ts` → `opciones()`), así que un 401 significa
 *    «vuelve a entrar» y el guard limpia la sesión.
 *  - Se conserva todo lo demás: construcción de query string, envoltura
 *    `{status, response}` de EDSuite API, lectura defensiva del cuerpo (si un
 *    proxy devuelve HTML, el error lo dice con un extracto) y timeout.
 */

import { env } from '@/config/env';

import { ApiError } from './api-error';
import type { ApiEnvelope, HttpMethod, QueryParams, RequestOptions } from './types';

function buildQuery(query?: QueryParams): string {
  if (!query) return '';

  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }

  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

function buildUrl(path: string, query?: QueryParams): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${env.apiUrl}${normalizedPath}${buildQuery(query)}`;
}

/**
 * Lee el cuerpo de la respuesta sin asumir JSON.
 *
 * Si un proxy o el servidor devuelve HTML/texto, se construye un error legible
 * con un extracto del body en lugar de fallar con un `SyntaxError` opaco.
 */
async function readPayload(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return null;

  let text: string;
  try {
    text = await response.text();
  } catch {
    return null;
  }

  if (!text) return null;

  const contentType = response.headers.get('content-type') ?? '';

  if (!contentType.includes('application/json')) {
    const preview = text.replace(/\s+/g, ' ').slice(0, 140);
    return {
      status: false,
      error: `El servidor devolvió una respuesta no-JSON (HTTP ${response.status}). ${preview}`,
      code: 'NON_JSON_RESPONSE',
    };
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return {
      status: false,
      error: 'La respuesta del servidor no pudo interpretarse como JSON.',
      code: 'INVALID_JSON',
    };
  }
}

function toApiError(response: Response, payload: unknown): ApiError {
  const envelope = (payload ?? {}) as Partial<ApiEnvelope<unknown>>;

  const message =
    typeof envelope.error === 'string' && envelope.error.trim() !== ''
      ? envelope.error
      : `La solicitud falló con estado ${response.status}`;

  return new ApiError(message, {
    status: response.status,
    code: envelope.code ?? `HTTP_${response.status}`,
    details: payload,
  });
}

function unwrapResponse<T>(response: Response, payload: unknown): T {
  if (!response.ok) throw toApiError(response, payload);

  const envelope = payload as Partial<ApiEnvelope<T>> | null;

  if (envelope && typeof envelope === 'object' && 'status' in envelope) {
    if (envelope.status === false) throw toApiError(response, payload);

    if ('response' in envelope) return envelope.response as T;

    return payload as T;
  }

  return payload as T;
}

interface SendResult {
  response: Response;
  payload: unknown;
}

class ApiClient {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  get baseURL(): string {
    return this.baseUrl;
  }

  get<T>(path: string, options?: Omit<RequestOptions, 'body'>): Promise<T> {
    return this.request<T>('GET', path, options);
  }

  post<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, options);
  }

  put<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, options);
  }

  patch<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, options);
  }

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, options);
  }

  /** Devuelve la envoltura completa (útil para leer `total`, `count`, etc.). */
  raw<T>(method: HttpMethod, path: string, options?: RequestOptions): Promise<ApiEnvelope<T>> {
    return this.send(method, path, options ?? {}).then(({ response, payload }) => {
      if (!response.ok) throw toApiError(response, payload);
      return (payload ?? { status: response.ok }) as ApiEnvelope<T>;
    });
  }

  async request<T>(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<T> {
    const { response, payload } = await this.send(method, path, options);

    return unwrapResponse<T>(response, payload);
  }

  private async send(
    method: HttpMethod,
    path: string,
    options: RequestOptions,
  ): Promise<SendResult> {
    const url = buildUrl(path, options.query);
    const headers = this.buildHeaders(options);

    const controller = new AbortController();
    const timeout = options.timeout ?? env.apiTimeout;
    const timeoutId = setTimeout(() => controller.abort(new Error('request-timeout')), timeout);

    // Si el consumidor pasa su propio signal, lo encadenamos al timeout.
    if (options.signal) {
      if (options.signal.aborted) controller.abort(options.signal.reason);
      else
        options.signal.addEventListener('abort', () => controller.abort(options.signal?.reason), {
          once: true,
        });
    }

    const hasBody = options.body !== undefined && method !== 'GET' && method !== 'DELETE';
    // Con `FormData` el navegador pone el `Content-Type` (con su `boundary`).
    const esFormData = options.body instanceof FormData;

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: hasBody ? (esFormData ? (options.body as FormData) : JSON.stringify(options.body)) : undefined,
        signal: controller.signal,
        credentials: 'omit',
        cache: 'no-store',
      });

      const payload = await readPayload(response);
      return { response, payload };
    } catch (error) {
      throw this.normalizeNetworkError(error, method, path);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private buildHeaders(options: RequestOptions): Headers {
    const headers = new Headers({ Accept: 'application/json' });

    if (options.body !== undefined && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    // El token del portal lo inyecta quien llama (`portal.api.ts`); aquí no hay
    // sesión global que consultar.
    for (const [key, value] of Object.entries(options.headers ?? {})) {
      headers.set(key, value);
    }

    return headers;
  }

  private normalizeNetworkError(error: unknown, method: HttpMethod, path: string): ApiError {
    const isAbort = error instanceof DOMException && error.name === 'AbortError';
    const reason = isAbort ? (error as DOMException & { cause?: unknown }).cause : null;
    const isTimeout = isAbort && reason instanceof Error && reason.message === 'request-timeout';

    if (isTimeout) {
      return new ApiError('La solicitud tardó demasiado. Intenta nuevamente.', {
        status: 0,
        code: 'TIMEOUT',
        details: { method, path },
      });
    }

    if (isAbort) {
      // Cancelación pedida por el consumidor (React Query al desmontar): se
      // re-lanza el AbortError original para que la librería la ignore.
      throw error;
    }

    return new ApiError('No se pudo conectar con el servidor.', {
      status: 0,
      code: 'NETWORK_ERROR',
      details: { method, path },
      cause: error,
    });
  }
}

/** Instancia única usada por todo el portal. */
export const apiClient = new ApiClient(env.apiUrl);
